import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { getVitalScoreHistory } from '@/utils/vitalScoreUtils';
import { useAuth } from '@/contexts/AuthContext';

const VitalScoreWeeklySummary = () => {
  const { user } = useAuth();
  const [vitalScoreData, setVitalScoreData] = useState<any[]>([]);
  
  useEffect(() => {
    loadVitalScoreData();
    
    // Listen for profile updates to refresh the chart
    const handleProfileUpdate = () => {
      console.log('VitalScoreWeeklySummary: Profile updated, refreshing data');
      loadVitalScoreData();
    };
    
    window.addEventListener('profile_updated', handleProfileUpdate);
    window.addEventListener('journal_goals_updated', handleProfileUpdate);
    window.addEventListener('menu_updated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profile_updated', handleProfileUpdate);
      window.removeEventListener('journal_goals_updated', handleProfileUpdate);
      window.removeEventListener('menu_updated', handleProfileUpdate);
    };
  }, [user?.id]);
  
  const loadVitalScoreData = () => {
    const vitalScoreHistory = getVitalScoreHistory();
    const today = new Date();
    
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = format(subDays(today, 6 - i), 'yyyy-MM-dd');
      const rawScore = vitalScoreHistory[date] || 0;
      
      // Enhanced validation to prevent NaN values
      let scoreValue = 0;
      if (typeof rawScore === 'number' && !isNaN(rawScore)) {
        scoreValue = rawScore;
      } else if (typeof rawScore === 'string') {
        // Try to parse string values
        const parsed = parseFloat(rawScore);
        if (!isNaN(parsed)) {
          scoreValue = parsed;
        }
      }
      
      // Ensure score is within valid range
      const score = Math.max(0, Math.min(100, scoreValue));
      
      return {
        date,
        score,
        formattedDate: format(subDays(today, 6 - i), 'EEE d', { locale: es })
      };
    });
    
    setVitalScoreData(last7Days);
    
    // Log vital score data for debugging
    console.log('Vital Score Weekly Summary Data:', last7Days);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const scoreValue = typeof payload[0].value === 'number' && !isNaN(payload[0].value)
        ? Math.max(0, Math.min(100, payload[0].value))
        : 0;
      
      return (
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-3 rounded-lg shadow-lg border border-purple-500/30 text-white">
          <p className="text-sm font-medium text-purple-300">{payload[0].payload.formattedDate}</p>
          <p className="text-md font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Vital Score: {scoreValue}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="mb-6 overflow-hidden bg-gradient-to-br from-purple-900/80 via-indigo-900/70 to-blue-900/80 backdrop-blur-xl border border-purple-500/20 shadow-xl">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent font-semibold">
          Evolución Semanal del Vital Score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={vitalScoreData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#9b87f5" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#8ed1fc" stopOpacity={0.4}/>
                </linearGradient>
                <linearGradient id="scoreStroke" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="5%" stopColor="#9b87f5" stopOpacity={1}/>
                  <stop offset="95%" stopColor="#33C3F0" stopOpacity={1}/>
                </linearGradient>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>
              <XAxis 
                dataKey="formattedDate" 
                tick={{ fontSize: 11, fill: '#d6bcfa' }}
                interval={0}
                stroke="#6E59A5"
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                domain={[0, 100]} 
                tick={{ fontSize: 11, fill: '#d6bcfa' }}
                stroke="#6E59A5"
                tickLine={false}
                axisLine={false}
              />
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="rgba(139, 92, 246, 0.2)"
                vertical={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone"
                dataKey="score" 
                stroke="url(#scoreStroke)"
                strokeWidth={3}
                dot={{ 
                  r: 4, 
                  fill: '#8B5CF6', 
                  stroke: '#1A1F2C', 
                  strokeWidth: 1,
                  filter: "url(#glow)" 
                }}
                activeDot={{ 
                  r: 8, 
                  fill: '#8ed1fc',
                  stroke: '#1A1F2C',
                  strokeWidth: 2,
                  filter: "url(#glow)" 
                }}
                fillOpacity={1}
                fill="url(#scoreGradient)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default VitalScoreWeeklySummary;
